import { create } from 'zustand';
import type { VoiceParticipantDTO } from '@clanfire/shared';

/* ------------------------------------------------------------------ */
/* Socket connection status                                            */
/* ------------------------------------------------------------------ */

type ConnectionStatus = 'offline' | 'connecting' | 'connected' | 'reconnecting';

export const useConnection = create<{ status: ConnectionStatus; setStatus: (s: ConnectionStatus) => void }>((set) => ({
  status: 'offline',
  setStatus: (status) => set({ status }),
}));

/* ------------------------------------------------------------------ */
/* Typing indicators                                                   */
/* ------------------------------------------------------------------ */

interface TypingState {
  byTarget: Record<string, Record<string, { displayName: string; expiresAt: number }>>;
  start: (key: string, userId: string, displayName: string, ttlMs: number) => void;
  stop: (key: string, userId: string) => void;
  prune: () => void;
  reset: () => void;
}

export const useTyping = create<TypingState>((set) => ({
  byTarget: {},
  start: (key, userId, displayName, ttlMs) =>
    set((s) => ({ byTarget: { ...s.byTarget, [key]: { ...s.byTarget[key], [userId]: { displayName, expiresAt: Date.now() + ttlMs } } } })),
  stop: (key, userId) =>
    set((s) => {
      if (!s.byTarget[key]?.[userId]) return s;
      const { [userId]: _removed, ...rest } = s.byTarget[key]!;
      return { byTarget: { ...s.byTarget, [key]: rest } };
    }),
  prune: () =>
    set((s) => {
      const now = Date.now();
      let changed = false;
      const byTarget: TypingState['byTarget'] = {};
      for (const [key, users] of Object.entries(s.byTarget)) {
        const kept = Object.fromEntries(Object.entries(users).filter(([, v]) => v.expiresAt > now));
        if (Object.keys(kept).length !== Object.keys(users).length) changed = true;
        byTarget[key] = kept;
      }
      return changed ? { byTarget } : s;
    }),
  reset: () => set({ byTarget: {} }),
}));

/* ------------------------------------------------------------------ */
/* Voice                                                               */
/* ------------------------------------------------------------------ */

export type VoiceStatus = 'idle' | 'connecting' | 'connected';

interface VoiceState {
  participants: Record<string, VoiceParticipantDTO[]>;
  speaking: Record<string, boolean>;
  channelId: string | null;
  serverId: string | null;
  status: VoiceStatus;
  muted: boolean;
  deafened: boolean;
  micUnavailable: boolean;
  /** Our camera is on. */
  video: boolean;
  localVideo: MediaStream | null;
  /** Incoming camera streams by user id. */
  remoteVideo: Record<string, MediaStream>;
  /** We're sharing our screen. */
  screen: boolean;
  localScreen: MediaStream | null;
  /** What we're sharing: a whole monitor, a window or a browser tab (when the browser says). */
  screenSurface: string | null;
  /** Incoming screen-share streams by user id. */
  remoteScreen: Record<string, MediaStream>;
  hydrate: (list: VoiceParticipantDTO[]) => void;
  setParticipants: (channelId: string, list: VoiceParticipantDTO[]) => void;
  removeChannel: (channelId: string) => void;
  setSpeaking: (userId: string, speaking: boolean) => void;
  setSelf: (patch: Partial<Pick<VoiceState, 'channelId' | 'serverId' | 'status' | 'muted' | 'deafened' | 'micUnavailable' | 'video' | 'localVideo' | 'screen' | 'localScreen' | 'screenSurface'>>) => void;
  setRemoteVideo: (userId: string, stream: MediaStream | null) => void;
  setRemoteScreen: (userId: string, stream: MediaStream | null) => void;
  reset: () => void;
}

export const useVoice = create<VoiceState>((set) => ({
  participants: {},
  speaking: {},
  channelId: null,
  serverId: null,
  status: 'idle',
  muted: false,
  deafened: false,
  micUnavailable: false,
  video: false,
  localVideo: null,
  remoteVideo: {},
  screen: false,
  localScreen: null,
  screenSurface: null,
  remoteScreen: {},
  setRemoteScreen: (userId, stream) =>
    set((s) => {
      if (stream) return { remoteScreen: { ...s.remoteScreen, [userId]: stream } };
      const { [userId]: _removed, ...rest } = s.remoteScreen;
      return { remoteScreen: rest };
    }),
  setRemoteVideo: (userId, stream) =>
    set((s) => {
      if (stream) return { remoteVideo: { ...s.remoteVideo, [userId]: stream } };
      const { [userId]: _removed, ...rest } = s.remoteVideo;
      return { remoteVideo: rest };
    }),
  hydrate: (list) => {
    const participants: Record<string, VoiceParticipantDTO[]> = {};
    for (const p of list) (participants[p.channelId] ??= []).push(p);
    const speaking = Object.fromEntries(list.filter((p) => p.speaking).map((p) => [p.userId, true]));
    set({ participants, speaking });
  },
  setParticipants: (channelId, list) =>
    set((s) => {
      const speaking = { ...s.speaking };
      for (const p of list) speaking[p.userId] = p.speaking;
      return { participants: { ...s.participants, [channelId]: list }, speaking };
    }),
  removeChannel: (channelId) =>
    set((s) => {
      const { [channelId]: _removed, ...participants } = s.participants;
      return { participants };
    }),
  setSpeaking: (userId, speaking) => set((s) => (s.speaking[userId] === speaking ? s : { speaking: { ...s.speaking, [userId]: speaking } })),
  setSelf: (patch) => set(patch),
  reset: () => set({ participants: {}, speaking: {}, channelId: null, serverId: null, status: 'idle', video: false, localVideo: null, remoteVideo: {}, screen: false, localScreen: null, screenSurface: null, remoteScreen: {} }),
}));
